import {ChangeDetectorRef, Component, OnDestroy} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Subject} from 'rxjs';
import {CommonService} from '@service/common/common.service';
import {VestaService} from '@service/vesta/vesta.service';
import {ConfigurationService} from '@service/configuration/configuration.service';
import {WalkerInput} from '@interface/composition/walker.mock';
import {ComponentsModule} from "../components.module";
import {MatTab, MatTabContent, MatTabGroup, MatTabLabel} from "@angular/material/tabs";
import {TranslatePipe} from "@ngx-translate/core";
import {MatTooltip} from "@angular/material/tooltip";
import {JsonPipe, UpperCasePipe} from "@angular/common";
import {MatRadioButton, MatRadioGroup} from "@angular/material/radio";
import {FormsModule} from "@angular/forms";

enum TabIndex {
  ELEMENTS = 0,
  CUSTOMIZE = 1,
  SUMMARY = 2,
}

interface ProductModel {
  id: number;
  name: string;
  lHook: boolean;
  rHook: boolean;
  cType: CatalogType;
  img?: string;
  dimensions: string;
}

interface CompElement {
  id: number;
  model: ProductModel;
}

type CatalogType = 'left' | 'central' | 'right';

type HookSide = 'L' | 'R' | '';

@Component({
  selector: 'app-composer',
  templateUrl: './composer.component.html',
  styleUrls: ['./composer.component.scss'],
  imports: [
    ComponentsModule,
    MatTabGroup,
    MatTab,
    TranslatePipe,
    MatTooltip,
    MatTabContent,
    MatRadioGroup,
    FormsModule,
    MatRadioButton,
    MatTabLabel
  ],
  standalone: true
})
export class ComposerComponent implements OnDestroy {
  private destroy$ = new Subject();
  loading = false;
  tabIndex = 0;
  tabs = [
    {id: 'elements', label: 'composition.elements-selection'},
    {id: 'customize', label: 'composition.customizing'},
    {id: 'summary', label: 'composition.summary'}
  ];
  cTabIndex = 0;
  customizeTabs = [
    {id: 'composition', label: 'composition.customize-all'},
    {id: 'elements', label: 'composition.customize-elements'},
  ];

  upholsteryType = 'Rivestimento Unico';
  upholsteryTypes: string[] = ['Rivestimento Unico', 'Rivestimento Separato'];

  models: ProductModel[] = [];

  composition: CompElement[] = [];

  selectedElem: CompElement | null = null;
  selectedHook: HookSide = '';

  leftHookVisible = false;
  rightHookVisible = false;

  private progrModelId = 0;
  private progrElemId = 0;


  enableVesta!: boolean;
  walkerVestaExternalLink!: string | null;

  /* mocked */
  vestaInput!: WalkerInput | null;

  estraiStrutturaTabSelezionato = JSON.parse(JSON.stringify(
    [
      {
        placementType: 'singlePlacement',
        idGroup: null,
        placementId: 77303,
        placementName: 'PIEDE',
        placementList: null,
        isOptional: false,
        vestaExternalId: 'WLKW66_IMB005_PLC0019',
        vestaIsEmpty: false,
        index: -1,
        configurableElement: false,
        hasMutuallyExclusiveOption: false,
        hideInConfigurationBar: true,
        optionalReplaceList: [],
        rulesList: false
      },
      {
        placementType: 'groupedPlacement',
        optional: false,
        idGroup: 7472,
        placementId: null,
        placementName: 'RIV. UNICO',
        placementList: [
          {
            placementType: 'singlePlacement',
            idGroup: null,
            placementId: 77034,
            placementName: 'Riv. Strutt. Princ.',
            placementList: null,
            isOptional: false,
            vestaExternalId: 'WLKW66_IMB001_PLC0122',
            vestaIsEmpty: false,
            index: 2,
            configurableElement: true,
            hasMutuallyExclusiveOption: false,
            hideInConfigurationBar: false,
            optionalReplaceList: [],
            rulesList: false
          },
          {
            placementType: 'singlePlacement',
            idGroup: null,
            placementId: 77035,
            placementName: 'RIV. CUSCINO VOL.',
            placementList: null,
            isOptional: false,
            vestaExternalId: 'WLKW66_IMB002_PLC0186',
            vestaIsEmpty: false,
            index: 3,
            configurableElement: true,
            hasMutuallyExclusiveOption: false,
            hideInConfigurationBar: false,
            optionalReplaceList: [],
            rulesList: false
          }
        ],
        vestaExternalId: null,
        index: -1,
        rulesList: false,
        hasMutuallyExclusiveOption: false
      }
    ]
  ));

  /* UI */
  scale = 1;
  readonly minScale = 0.5;
  readonly maxScale = 2;
  readonly step = 0.05;

  get transform(): string {
    return `scale(${this.scale})`;
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();

    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  zoomIn(): void {
    this.scale = Math.min(this.scale + this.step, this.maxScale);
  }

  zoomOut(): void {
    this.scale = Math.max(this.scale - this.step, this.minScale);
  }

  constructor(
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly commonService: CommonService,
    private readonly vestaService: VestaService,
    private readonly configurationService: ConfigurationService,
    private readonly cd: ChangeDetectorRef
  ) {
    this.bootstrapVesta();
    this.populateModels();
    this.updateSelection(this.selectedHook);
  }

  ngOnDestroy() {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  bootstrapVesta(): void {
    this.enableVesta = true;
    this.walkerVestaExternalLink = 'mrp_bom-4075'; /* For mock purpose only */
    this.vestaInput = this.configurationService.createMockTeapot() as WalkerInput;
  }

  /* ----- INIT / CREAZIONE DATI ----- */

  private createModel(
    name: string,
    l: boolean,
    r: boolean,
    t: CatalogType,
    dim: string,
    img?: string,
  ): ProductModel {
    return {
      id: this.progrModelId++,
      name,
      lHook: l,
      rHook: r,
      cType: t,
      dimensions: dim,
      img,
    };
  }

  private createElement(model: ProductModel): CompElement {
    const id = this.progrElemId++;
    return {
      id,
      model,
    };
  }

  private populateModels(): void {
    // this.models.push(this.createModel('WLKW43', false, true, 'left', 'L 182 x H 64 x W 116 cm', 'assets/composer-mock/images/WLKW43.png'));
    // this.models.push(this.createModel('WLKW44', true, false, 'right', 'L 182 x H 64 x W 116 cm', 'assets/composer-mock/images/WLKW44.png'));
    // this.models.push(this.createModel('WLKW49', false, true, 'left', 'L 137 x H 64 x W 116 cm', 'assets/composer-mock/images/WLKW49.png'));
    // this.models.push(this.createModel('WLKW50', true, false, 'right', 'L 137 x H 64 x W 116 cm', 'assets/composer-mock/images/WLKW50.png'));
    // this.models.push(this.createModel('WLKW53', true, true, 'central', 'L 124 x H 64 x W 116 cm', 'assets/composer-mock/images/WLKW53.png'));
    // this.models.push(this.createModel('WLKW57', true, true, 'central', 'L 89 x H 64 x W 116 cm', 'assets/composer-mock/images/WLKW57.png'));
    // this.models.push(this.createModel('WLKW69', true, true, 'central', 'L 99 x H 42 x W 105 cm', 'assets/composer-mock/images/WLKW69.png'));
    // this.models.push(this.createModel('WLKW74', true, false, 'right', 'L 87 x H 42 x W 105 cm', 'assets/composer-mock/images/WLKW74.png'));
  }

  /* ----- LOGICA DI SELEZIONE / HOOK ----- */

  private updateSelection(side: HookSide): void {
    this.leftHookVisible = false;
    this.rightHookVisible = false;
    this.selectedHook = '';
    this.selectedElem = null;

    if (this.composition.length > 0) {
      this.leftHookVisible = this.composition[0].model.lHook;
      this.rightHookVisible = this.composition[this.composition.length - 1].model.rHook;

      if (side === 'L') {
        this.selectedElem = this.composition[0];
        if (this.leftHookVisible) {
          this.selectedHook = 'L';
        }
      } else if (side === 'R') {
        this.selectedElem = this.composition[this.composition.length - 1];
        if (this.rightHookVisible) {
          this.selectedHook = 'R';
        }
      }

      if (this.selectedHook === '') {
        if (this.leftHookVisible) {
          this.selectedHook = 'L';
        }
        if (this.rightHookVisible) {
          this.selectedHook = 'R';
        }
      }
    }
  }

  private isCompatible(model: ProductModel, side: HookSide): boolean {
    return (side === 'R' && model.lHook) || (side === 'L' && model.rHook);
  }

  /* ----- ACTIONS DAL CATALOGO ----- */

  onAddModel(model: ProductModel): void {
    this.loading = true;

    setTimeout(() => {
      this.addModelInternal(model);
      this.loading = false;
    }, 1000);
  }

  public added(model: ProductModel): any {
    if (this.composition.length) {
      return this.composition.find((elem) => elem.model.id === model.id);
    }
    return false;
  }

  public quantity(model: ProductModel): number {
    if (this.composition.length) {
      return this.composition.filter((elem) => elem.model.name === model.name)?.length || 0;
    }
    return 0;
  }

  private addModelInternal(model: ProductModel): void {
    const elem = this.createElement(model);

    if (this.composition.length === 0) {
      this.composition.push(elem);
      this.updateSelection(this.selectedHook);
      this.bootstrapVesta();
      return;
    }

    if (this.selectedHook && this.isCompatible(model, this.selectedHook)) {
      if (this.selectedHook === 'R') {
        this.composition.push(elem);
      } else {
        this.composition.unshift(elem);
      }
      this.updateSelection(this.selectedHook);
    }
  }

  /* ----- DELETE ----- */

  onDeleteSelected(): void {
    if (!this.selectedElem) {
      return;
    }

    const idx = this.elemIndex(this.selectedElem.id);
    if (idx === null) {
      return;
    }

    if (this.isFirstElem(this.selectedElem.id)) {
      this.composition.shift();
      this.updateSelection('L');
    } else if (this.isLastElem(this.selectedElem.id)) {
      this.composition.pop();
      this.updateSelection('R');
    } else {
      this.composition.splice(idx, 1);
      this.updateSelection('');
    }
  }

  /* ----- UTILITIES ----- */

  private elemIndex(id: number): number | null {
    const idx = this.composition.findIndex((e) => e.id === id);
    return idx === -1 ? null : idx;
  }

  private isFirstElem(id: number): boolean {
    return this.elemIndex(id) === 0;
  }

  private isLastElem(id: number): boolean {
    return this.elemIndex(id) === this.composition.length - 1;
  }

  /* ----- CLICK SU ELEMENTI / HOOK ----- */

  onSelectElement(elem: CompElement): void {
    this.selectedElem = elem;

    if (this.isFirstElem(elem.id) && elem.model.lHook) {
      this.selectedHook = 'L';
    } else if (this.isLastElem(elem.id) && elem.model.rHook) {
      this.selectedHook = 'R';
    }

    this.goToTab(0);
  }

  undoSelectElem(): void {
    this.selectedElem = null;
    this.selectedHook = '';
  }

  onSelectHook(side: HookSide): void {
    this.selectedHook = side;

    if (side === 'L') {
      this.selectedElem = this.composition[0] ?? null;
    } else if (side === 'R') {
      this.selectedElem = this.composition[this.composition.length - 1] ?? null;
    }
  }

  /* ----- 3D ----- */

  private startVestaMockedTeapot(): void {
    if (this.enableVesta) {
      this.commonService.vestaInput.next({struttura: this.vestaInput?.struttura, configurazione: true});
      setTimeout(() => {
        this.commonService.vestaInput.next({struttura: this.vestaInput?.struttura, configurazione: true});
        this.commonService.vestaInput.next({
          struttura: this.configurationService.createMockGroupedStructure(),
          forzaRaggruppamento: true
        });

      }, 5000);
      // this.commonService.vestaInput.next({struttura: this.configurationService.createMockGroupedStructure(), forzaRaggruppamento: true});
      // this.vestaService.aggiornaStrutturaConfigurata(this.estraiStrutturaTabSelezionato);

    }
    this.cd.markForCheck();
  }

  /* ----- GETTER PER TEMPLATE ----- */

  get leftCatalog(): ProductModel[] {
    return this.models.filter((m) => !m.lHook && m.rHook);
  }

  get centralCatalog(): ProductModel[] {
    return this.models.filter((m) => m.lHook && m.rHook);
  }

  get rightCatalog(): ProductModel[] {
    return this.models.filter((m) => m.lHook && !m.rHook);
  }

  isSelectedElement(elem: CompElement): boolean {
    return this.selectedElem != null && this.selectedElem.id === elem.id;
  }

  public get showLeftHook(): boolean {
    return this.leftHookVisible;
  }

  public get showRightHook(): boolean {
    return this.rightHookVisible;
  }

  public get isLeftHookSelected(): boolean {
    return this.selectedHook === 'L';
  }

  public get isRightHookSelected(): boolean {
    return this.selectedHook === 'R';
  }

  public get fullChainReached(): boolean {
    return !!this.composition.length && !this.showLeftHook && !this.showRightHook;
  }

  preventTabChange(tab: any): boolean {
    return !!(tab.id !== 'elements' && (!this.composition.length || this.selectedElem));
  }

  goToTab(index: number): void {
    this.tabIndex = index;
    switch (index) {
      case TabIndex.ELEMENTS:
        this.bootstrapVesta();
        break;
      case TabIndex.CUSTOMIZE:
        this.undoSelectElem();
        this.startVestaMockedTeapot();
        break;
    }
  }

  isElementSelectable(elem: CompElement): boolean {
    if (!this.composition.length) {
      return false;
    }
    const first = this.composition[0];
    const last = this.composition[this.composition.length - 1];
    return elem.id === first.id || elem.id === last.id;
  }

}
